import React, { useState, useCallback } from 'react'
import { geoService } from '../services/geoService'
import WorldMapSVG from '../assets/WorldMapBackground.svg';

// Color palette for versions
const COLORS = [
  "#3b82f6", "#10b981", "#f59e42", "#ef4444", "#a78bfa", "#f472b6", "#facc15", "#38bdf8", "#34d399", "#fb7185"
];

const WorldMap = ({ data, locations }) => {
  const mapWidth = 800
  const mapHeight = 400
  const [zoom, setZoom] = useState(1)
  const [pan, setPan] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const [tooltip, setTooltip] = useState(null)

  // Filter for the last hour
  const oneHourAgo = Date.now() - 60 * 60 * 1000
  const filteredData = data.filter(item => {
    const ts = new Date(item.timestamp).getTime()
    return ts >= oneHourAgo
  })

  // Keep only the latest entry per IP
  const latestByIP = {}
  filteredData.forEach(item => {
    const ip = item.client_ip
    const ts = new Date(item.timestamp).getTime()
    if (!latestByIP[ip] || ts > new Date(latestByIP[ip].timestamp).getTime()) {
      latestByIP[ip] = item
    }
  })
  const uniqueData = Object.values(latestByIP)

  // Grouper les données par IP (now only one per IP)
  const ipCounts = uniqueData.reduce((acc, item) => {
    acc[item.client_ip] = 1
    return acc
  }, {})

  // Get unique versions in the data
  const uniqueVersions = Array.from(new Set(uniqueData.map(item => item.json_data?.version || "N/A")));

  // Map each version to a color
  const versionColorMap = {};
  uniqueVersions.forEach((version, idx) => {
    versionColorMap[version] = COLORS[idx % COLORS.length];
  });

  const handleWheel = useCallback((e) => {
    e.preventDefault()
    
    // Get mouse position relative to the SVG
    const svg = e.currentTarget
    const rect = svg.getBoundingClientRect()
    const mouseX = e.clientX - rect.left
    const mouseY = e.clientY - rect.top
    
    // Calculate mouse position in the transformed coordinate system
    const transformedX = (mouseX - pan.x) / zoom
    const transformedY = (mouseY - pan.y) / zoom
    
    // Calculate zoom factor
    const delta = e.deltaY > 0 ? 0.9 : 1.1
    const newZoom = Math.min(Math.max(zoom * delta, 1), 5)
    
    // Calculate new pan position to keep mouse point fixed
    const newPanX = mouseX - transformedX * newZoom
    const newPanY = mouseY - transformedY * newZoom
    
    setZoom(newZoom)
    setPan({ x: newPanX, y: newPanY })
  }, [zoom, pan])

  const handleMouseDown = useCallback((e) => {
    setIsDragging(true)
    setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y })
  }, [pan])

  const handleMouseMove = useCallback((e) => {
    if (isDragging) {
      setPan({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      })
    }
  }, [isDragging, dragStart])

  const handleMouseUp = useCallback(() => {
    setIsDragging(false)
  }, [])

  const handlePointClick = useCallback((e, ip, version) => {
    e.stopPropagation() // Prevent map drag
    const svg = e.currentTarget.ownerSVGElement
    const rect = svg.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    setTooltip({ x, y, ip, version })
  }, [])

  const handleSvgClick = useCallback(() => {
    setTooltip(null)
  }, [])

  return (
    <div className="bg-white rounded-lg border overflow-hidden">
      <div className="p-4 border-b">
        <h3 className="font-semibold">Geographic Distribution</h3>
        <p className="text-sm text-gray-600">
          {uniqueData.length} active nodes (last hour)
        </p>
      </div>
      
      <div className="p-4">
        <svg 
          width="100%" 
          height={mapHeight} 
          viewBox={`0 0 ${mapWidth} ${mapHeight}`} 
          className="border rounded cursor-grab"
          onWheel={handleWheel}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onClick={handleSvgClick}
        >
          <g transform={`translate(${pan.x}, ${pan.y}) scale(${zoom})`}>
            {/* World map SVG as background image */}
            <image
              href={WorldMapSVG}
              x="0"
              y="0"
              width={mapWidth}
              height={mapHeight}
              preserveAspectRatio="xMidYMid slice"
            />
            
            {/* Points for each IP */}
            {uniqueData.map(item => {
              const ip = item.client_ip
              const location = locations[ip]
              if (!location) return null
              const { x, y } = geoService.projectToMap(location.lat, location.lon, mapWidth, mapHeight)
              const version = item.json_data?.version || "N/A"
              const color = versionColorMap[version]
              const baseRadius = 8 // Base size for better visibility
              const radius = baseRadius / Math.sqrt(zoom) // Scale radius inversely with zoom
              return (
                <g 
                  key={ip}
                  onClick={(e) => handlePointClick(e, ip, version)}
                  className="cursor-pointer"
                >
                  <circle
                    cx={x}
                    cy={y}
                    r={radius}
                    fill={color}
                    opacity="0.7"
                    stroke="#1d4ed8"
                    strokeWidth="1"
                  />
                  <circle
                    cx={x}
                    cy={y}
                    r={radius + 3}
                    fill="none"
                    stroke={color}
                    strokeWidth="1"
                    opacity="0.4"
                  >
                    <animate attributeName="r" values={`${radius + 3};${radius + 8};${radius + 3}`} dur="2s" repeatCount="indefinite" />
                    <animate attributeName="opacity" values="0.4;0.1;0.4" dur="2s" repeatCount="indefinite" />
                  </circle>
                </g>
              )
            })}
          </g>
          
          {/* Legend with colors for each version */}
          <g transform={`translate(5, ${mapHeight - 30 - 22 * uniqueVersions.length})`}>
            <rect width="220" height={30 + 22 * uniqueVersions.length} fill="white" opacity="0.9" rx="5" stroke="#e5e7eb"/>
            <text x="10" y="20" fontSize="12" fontWeight="bold" fill="#374151">Legend</text>
            {uniqueVersions.map((version, idx) => (
              <g key={version}>
                <circle cx="20" cy={35 + idx * 22} r="8" fill={versionColorMap[version]} />
                <text x="35" y={40 + idx * 22} fontSize="12" fill="#6b7280">{version}</text>
              </g>
            ))}
          </g>

          {/* Tooltip */}
          {tooltip && (
            <g transform={`translate(${tooltip.x}, ${tooltip.y})`}>
              <rect
                x="-100"
                y="-40"
                width="200"
                height="40"
                fill="white"
                opacity="0.95"
                rx="5"
                stroke="#e5e7eb"
              />
              <text x="-90" y="-20" fontSize="12" fill="#374151">
                IP: {tooltip.ip}
              </text>
              <text x="-90" y="-5" fontSize="12" fill="#374151">
                Version: {tooltip.version}
              </text>
            </g>
          )}
        </svg>
      </div>
    </div>
  )
}

export default WorldMap 